class CanvasDrawer {
  constructor(canvas, startPoint, padding) {
    this.canvas = canvas;
    this.context = canvas.getContext('2d');
    this.padding = padding || { left: 10, top: 1.5 };

    // init top left corner starting point
    this.startPoint = startPoint || { x: 0, y: 0 };
    this.devicePixelRatio = window.devicePixelRatio || 1;
    this.adjustForHighDPI();
  }

  setStartPoint({ x, y }) {
    this.startPoint = { x, y };
  }

  clearCanvas() {
    this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }

  // Adapted to high resolution screens
  adjustForHighDPI() {
    const width = this.canvas.width;
    const height = this.canvas.height;

    this.canvas.width = width * this.devicePixelRatio;
    this.canvas.height = height * this.devicePixelRatio;
    this.canvas.style.width = width + 'px';
    this.canvas.style.height = height + 'px';

    // Zoom plot context
    this.context.scale(this.devicePixelRatio, this.devicePixelRatio);
  }

  //Resize the canvas
  adjustCanvasSize(width, height, padding) {
    if (width) {
      this.canvas.width = width * this.devicePixelRatio;
      this.canvas.style.width = width + 'px';
    }
    this.canvas.height = height * this.devicePixelRatio;
    this.canvas.style.height = height + 'px';
    this.padding = padding || { left: 10, top: 1.5 };
    this.context.scale(this.devicePixelRatio, this.devicePixelRatio);
  }

  drawText({ x, y, text, font = '16px Arial', fillStyle = 'black', textAlign = 'center', textBaseline = 'middle' }) {
    this.context.font = font; // set font size and style
    this.context.fillStyle = fillStyle; // fill color
    this.context.textAlign = textAlign; // Text alignment
    this.context.textBaseline = textBaseline; // Text baseline alignment

    this.context.fillText(text, x + this.padding.left + this.startPoint.x, y + this.padding.top + this.startPoint.y);
  }

  drawLine({ x1, y1, x2, y2, color = '#005ab3', lineWidth = 1.5 }) {
    this.context.beginPath();
    this.context.moveTo(x1 + this.padding.left + this.startPoint.x, y1 + this.padding.top + this.startPoint.y);
    this.context.lineTo(x2 + this.padding.left + this.startPoint.x, y2 + this.padding.top + this.startPoint.y);
    this.context.strokeStyle = color;
    this.context.lineWidth = lineWidth / this.devicePixelRatio;;
    this.context.stroke();
    this.context.closePath();
  }

  drawTriangle({ x1, y1, x2, y2, x3, y3, color = 'black', fillColor = null, lineWidth = 1.5 }) {
    this.context.beginPath();
    this.context.moveTo(x1 + this.padding.left + this.startPoint.x, y1 + this.padding.top + this.startPoint.y);
    this.context.lineTo(x2 + this.padding.left + this.startPoint.x, y2 + this.padding.top + this.startPoint.y);
    this.context.lineTo(x3 + this.padding.left + this.startPoint.x, y3 + this.padding.top + this.startPoint.y);
    this.context.closePath();

    if (fillColor) {
      this.context.fillStyle = fillColor;
      this.context.fill();
    }

    this.context.strokeStyle = color;
    this.context.lineWidth = lineWidth / this.devicePixelRatio;;
    this.context.stroke();
  }

  //  x,y is the top-left coordinate
  drawRectangle({ x, y, width, height, color = 'transparent', fillColor = '', lineWidth = 1.5, radius = 2 }) {
    this.context.beginPath();
    this.context.moveTo(x + this.padding.left + this.startPoint.x + radius, y + this.padding.top + this.startPoint.y);

    this.context.arcTo(x + this.padding.left + this.startPoint.x + width, y + this.padding.top + this.startPoint.y, x + this.padding.left + this.startPoint.x + width, y + this.padding.top + this.startPoint.y + height, radius);   // 右上角圆角
    this.context.arcTo(x + this.padding.left + this.startPoint.x + width, y + this.padding.top + this.startPoint.y + height, x + this.padding.left + this.startPoint.x, y + this.padding.top + this.startPoint.y + height, radius);  // 右下角圆角
    this.context.arcTo(x + this.padding.left + this.startPoint.x, y + this.padding.top + this.startPoint.y + height, x + this.padding.left + this.startPoint.x, y + this.padding.top + this.startPoint.y, radius);                   // 左下角圆角
    this.context.arcTo(x + this.padding.left + this.startPoint.x, y + this.padding.top + this.startPoint.y, x + this.padding.left + this.startPoint.x + width, y + this.padding.top + this.startPoint.y, radius);                    // 左上角圆角

    if (fillColor) {
      this.context.fillStyle = fillColor;
      this.context.fill();
    }

    this.context.strokeStyle = color;
    this.context.lineWidth = lineWidth / this.devicePixelRatio;
    this.context.stroke();
    this.context.closePath();
  }

  drawCircle({ x, y, radius = 5, color = '#bfbfbf', fillColor = null }) {
    this.context.beginPath();
    this.context.arc(x + this.padding.left + this.startPoint.x, y + this.padding.top + this.startPoint.y, radius, 0, 2 * Math.PI);

    if (fillColor) {
      this.context.fillStyle = fillColor;
      this.context.fill();
    }

    this.context.strokeStyle = color;
    this.context.stroke();
    this.context.closePath();
  }

  /**
     * Draw an unsealed semi-arc
     * @param {number} x x-coordinate of the center of the circle
     * @param {number} y y-coordinate of the center of the circle
     * @param {number} radius radius of the circle
     */
  drawArc({ x, y, radius = 6, startAngle = Math.PI, endAngle = 0, strokeColor = '#005ab3', lineWidth = 1.5 }) {
    this.context.beginPath();
    this.context.arc(x + this.padding.left + this.startPoint.x, y + this.padding.top + this.startPoint.y, radius, startAngle, endAngle);
    if (strokeColor) {
      this.context.strokeStyle = strokeColor;
      this.context.lineWidth = lineWidth;
      this.context.stroke();
    }
  }

  drawPolyline({ points, strokeStyle = '#005ab3', lineWidth = 1.5, closePath = false, fillStyle = null }) {
    if (points.length < 2) return;

    this.context.beginPath();
    this.context.moveTo(points[0][0] + this.padding.left + this.startPoint.x, points[0][1] + this.padding.top + this.startPoint.y);
    for (let i = 1; i < points.length; i++) {
      this.context.lineTo(points[i][0] + this.padding.left + this.startPoint.x, points[i][1] + this.padding.top + this.startPoint.y);
    }

    if (closePath) {
      this.context.closePath();
      if (fillStyle) {
        this.context.fillStyle = fillStyle;
        this.context.fill();
      }
    }

    this.context.strokeStyle = strokeStyle;
    this.context.lineWidth = lineWidth;
    this.context.stroke();
  }
}

function getDrawer(id, startPoint, padding) {
  const canvas = document.getElementById(id);
  if (!canvas) {
    console.error(`Canvas with id ${id} not found.`);
    return {};
  }
  const drawer = new CanvasDrawer(canvas, startPoint, padding);
  return { drawer, canvas };
}

function drawModel({ drawer, isShowCtle, isShowRxCpad, isShowRx, isShowStimulus, txExpandConnectWidth = 0, rxExpandConnectWidth = 0, expandModelConnectVisible }) {

  let expandModelConnectWidth = txExpandConnectWidth + rxExpandConnectWidth;
  if (expandModelConnectVisible) {
    expandModelConnectWidth += 50;
  } else {
    expandModelConnectWidth = 0;
  }

  // Tx triangle, 3
  drawTXTriangleHelper({
    drawer,
    txPadding: { left: isShowStimulus ? 130 : 30, top: 25 },
    txGap: { left: 0, top: 5 },
    firstTxTriangle: { x1: 1, y1: 1, x2: 47, y2: 31, x3: 1, y3: 61 },
    lineLength: (isShowStimulus ? 223 : 323) + (txExpandConnectWidth > 0 ? txExpandConnectWidth + 50 : 0),
    high: 60,
    isShowStimulus
  });

  // termination
  const zPadding = { left: 860 + expandModelConnectWidth, top: 50 };
  const zGap = { left: 0, top: 40 }
  const zLineList = [
    { x1: 0, y1: 0, x2: 47, y2: 31, x3: 0, y3: 61 },
    { x1: 0, y1: 61, x2: 47, y2: 91, x3: 0, y3: 121 },
    { x1: 0, y1: 121, x2: 47, y2: 151, x3: 0, y3: 181 }
  ]
  const lineInfoChannelToZ = {
    hLineLength: 15,
    vLineLength: 60,
    lineNumber: 3
  }
  const zhline = 15

  // connnect Channel - Ctle
  const lineInfoChannelToCtle = {
    lineLength: 580 + rxExpandConnectWidth,
    lineNumber: 3,
    lineGap: 100
  }

  const startNode = { x: 402 + (expandModelConnectVisible ? txExpandConnectWidth + 50 : 0), y: 21 }
  for (let i = 0; i < lineInfoChannelToCtle.lineNumber; i++) {
    const basicY = startNode.y + lineInfoChannelToCtle.lineGap * i;
    // left horizontal line
    if (i === 0) {
      drawer.drawLine({
        x1: startNode.x,
        y1: basicY,
        x2: startNode.x + lineInfoChannelToCtle.lineLength,
        y2: basicY,
      })
    } else {
      drawer.drawLine({
        x1: startNode.x,
        y1: basicY,
        x2: zLineList[0].x2 + zPadding.left - 1 + zhline - 10 - 6,
        y2: basicY,
      })
      // arc
      drawer.drawArc({ x: zLineList[0].x2 + zPadding.left - 1 + zhline - 10, y: basicY });
      // Right horizontal line
      drawer.drawLine({
        x1: zLineList[0].x2 + zPadding.left - 1 + zhline - 10 + 6,
        y1: basicY,
        x2: startNode.x + lineInfoChannelToCtle.lineLength,
        y2: basicY,
      })
    }
  }

  // Rx
  const rxPadding = { left: 580 + expandModelConnectWidth, top: 50 };
  const rxGap = { left: 80, top: 40 }
  const rxTriangles = [
    { x1: 1, y1: 1, x2: 47, y2: 31, x3: 1, y3: 61 },
    { x1: 1, y1: 61, x2: 47, y2: 91, x3: 1, y3: 121 },
    { x1: 1, y1: 121, x2: 47, y2: 151, x3: 1, y3: 181 }

  ];
  const rxCpadPadding = { left: rxPadding.left + rxGap.left + 50, top: 0 };
  // connect Rx - Channel
  // horizon ——
  // vertical |
  const lineInfoChannelToRx = {
    hLineLength: 90,
    vLineLength: 60,
    lineNumber: 3
  }

  const lineInfoChannelToCPad = {
    hLineLength: 30,
    vLineLength: 40,
    // Vertical lines connecting triangles
    vLineLength2: 20,
    lineNumber: 3,
    hLineGap: 5
  }

  for (let i in rxTriangles) {
    const info = rxTriangles[i];
    if (isShowRx) {
      const moveX = rxPadding.left + rxGap.left, moveY = rxPadding.top + rxGap.top * i;
      drawer.drawTriangle({
        x1: info.x1 + moveX,
        y1: info.y1 + moveY,
        x2: info.x2 + moveX,
        y2: info.y2 + moveY,
        x3: info.x3 + moveX,
        y3: info.y3 + moveY
      });

      // connect line - Rx
      // horizontal line
      drawer.drawLine({
        x1: info.x2 + moveX - 46,
        y1: info.y2 + moveY,
        x2: info.x2 + moveX - 46 - lineInfoChannelToRx.hLineLength,
        y2: info.y2 + moveY
      });
      // vertical line
      drawer.drawLine({
        x1: info.x2 + moveX - 46 - lineInfoChannelToRx.hLineLength,
        y1: info.y2 + moveY,
        x2: info.x2 + moveX - 46 - lineInfoChannelToRx.hLineLength,
        y2: info.y2 + moveY - lineInfoChannelToRx.vLineLength
      });
    }

    // draw RxCpad
    if (isShowRxCpad) {
      const basicY = startNode.y + lineInfoChannelToCtle.lineGap * i;
      const basicX = info.x2 + rxCpadPadding.left;
      // channel to ctle Vertical connection line
      drawer.drawLine({
        x1: basicX,
        y1: basicY,
        x2: basicX,
        y2: basicY + lineInfoChannelToCPad.vLineLength
      });
      // Two horizontal connecting lines
      drawer.drawLine({
        x1: basicX - lineInfoChannelToCPad.hLineLength / 2,
        y1: basicY + lineInfoChannelToCPad.vLineLength,
        x2: basicX + lineInfoChannelToCPad.hLineLength / 2,
        y2: basicY + lineInfoChannelToCPad.vLineLength
      });
      drawer.drawLine({
        x1: basicX - lineInfoChannelToCPad.hLineLength / 2,
        y1: basicY + lineInfoChannelToCPad.vLineLength + lineInfoChannelToCPad.hLineGap,
        x2: basicX + lineInfoChannelToCPad.hLineLength / 2,
        y2: basicY + lineInfoChannelToCPad.vLineLength + lineInfoChannelToCPad.hLineGap
      });
      // Vertical lines connecting triangles
      const basicY1 = basicY + lineInfoChannelToCPad.vLineLength + lineInfoChannelToCPad.hLineGap;
      drawer.drawLine({
        x1: basicX,
        y1: basicY1,
        x2: basicX,
        y2: basicY1 + lineInfoChannelToCPad.vLineLength2
      });
      const triangleY = basicY1 + lineInfoChannelToCPad.vLineLength2;
      drawer.drawTriangle({
        x1: basicX - 8,
        y1: triangleY,
        x2: basicX + 8,
        y2: triangleY,
        x3: basicX,
        y3: triangleY + 8,
        color: '#005ab3'
      });
    }
  }

  // termination
  for (let i in zLineList) {
    const info = zLineList[i];
    const moveX = zPadding.left + zGap.left * i, moveY = zPadding.top + zGap.top * i;
    // Resistance radian
    const startPoint = {
      x: info.x2 + moveX - 46,
      y: info.y2 + moveY
    }

    const pointGap = { x: 5, y: 7 }
    for (let j = 1; j < 6; j++) {
      let x1 = startPoint.x + j * pointGap.x, x2 = startPoint.x + (j + 1) * pointGap.x;
      const y1 = startPoint.y + pointGap.y, y2 = startPoint.y - pointGap.y

      if (j === 1) {
        drawer.drawLine({ x1: startPoint.x, y1: startPoint.y, x2: x1, y2 });
      } else if (j === 5) {
        drawer.drawLine({ x1: x2, y1, x2: x2 + pointGap.x, y2: startPoint.y });
        // Horizontal line segments
        drawer.drawLine({ x1: x2 + pointGap.x + zhline, y1: startPoint.y, x2: x2 + pointGap.x, y2: startPoint.y });
      }
      if (j % 2 !== 0) {
        x1 = startPoint.x + (j + 1) * pointGap.x;
        x2 = startPoint.x + j * pointGap.x;
      }
      drawer.drawLine({ x1, y1, x2, y2 });
    }

    // line - Rx
    drawer.drawLine({
      x1: info.x2 + moveX - 46,
      y1: info.y2 + moveY,
      x2: info.x2 + moveX - 46 - lineInfoChannelToZ.hLineLength,
      y2: info.y2 + moveY
    });

    drawer.drawLine({
      x1: info.x2 + moveX - 46 - lineInfoChannelToZ.hLineLength,
      y1: info.y2 + moveY,
      x2: info.x2 + moveX - 46 - lineInfoChannelToZ.hLineLength,
      y2: info.y2 + moveY - lineInfoChannelToZ.vLineLength
    });

    // draw ctle right，To modify the origin position
    const lineInfoCtle = {
      lineLength: 40,
      lineNumber: 3,
      lineGap: 100
    }
    const ctleStartNode = { x: (isShowCtle ? 1180 : 939) + expandModelConnectWidth, y: 21 }
    for (let i = 0; i < lineInfoCtle.lineNumber; i++) {
      if (isShowCtle) {
        drawer.drawLine({
          x1: ctleStartNode.x,
          y1: ctleStartNode.y + lineInfoCtle.lineGap * i,
          x2: ctleStartNode.x + lineInfoCtle.lineLength,
          y2: ctleStartNode.y + lineInfoCtle.lineGap * i
        })
      }
      // draw Circle
      drawer.drawCircle({
        x: ctleStartNode.x + lineInfoCtle.lineLength,
        y: ctleStartNode.y + lineInfoCtle.lineGap * i,
        radius: 5,
        color: '#f91414',
        fillColor: '#f91414'
      })
    }
  }

  // termination，The right segment is grounded
  const z_h_LineLength = 30, z_v_LineLength1 = 350, z_v_LineLength2 = 20;

  const z_h_Gap = 5;
  const v_x1 = zLineList[0].x2 + zPadding.left - 1 + zhline - 10,
    v_x2 = zLineList[0].x2 + zPadding.left - 1 + zhline - 10,
    v_y1 = zLineList[0].y2 + zPadding.top,
    v_y2 = z_v_LineLength1;
  // vertical 1
  drawer.drawLine({ x1: v_x1, y1: v_y1, x2: v_x2, y2: v_y2 })
  // horizontal 1
  drawer.drawLine({
    x1: v_x1 - z_h_LineLength / 2,
    y1: v_y2,
    x2: v_x2 + z_h_LineLength / 2,
    y2: v_y2
  })
  // horizontal 2
  drawer.drawLine({
    x1: v_x1 - z_h_LineLength / 2,
    y1: v_y2 + z_h_Gap,
    x2: v_x1 + z_h_LineLength / 2,
    y2: v_y2 + z_h_Gap,
  })
  // vertical 2
  drawer.drawLine({
    x1: v_x2,
    y1: v_y2 + z_h_Gap,
    x2: v_x2,
    y2: v_y2 + z_h_Gap + z_v_LineLength2
  })
  drawer.drawTriangle({
    x1: v_x2 - 8,
    y1: v_y2 + 25,
    x2: v_x2 + 8,
    y2: v_y2 + 25,
    x3: v_x2,
    y3: v_y2 + 33,
    color: '#005ab3'
  })
}

function drawHelper({
  drawer,
  signals,
  width,
  height,
  isShowCtle,
  isShowRxCpad,
  isShowRx,
  isShowStimulus,
  padding,
  txExpandConnectWidth,
  rxExpandConnectWidth,
  expandModelConnectVisible
}) {
  drawer.clearCanvas();
  drawer.adjustCanvasSize(width, height, padding)

  if (!signals || signals.length === 0) {
    return;
  }

  for (const i in signals) {
    drawer.setStartPoint({ x: 0, y: 400 * i });
    drawModel({ drawer, isShowCtle, isShowRxCpad, isShowRx, isShowStimulus, txExpandConnectWidth, rxExpandConnectWidth, expandModelConnectVisible });
  }

  drawer.setStartPoint({ x: 0, y: 0 });
}

function drawTxModelHelper({ drawer }) {
  drawer.drawRectangle({ x: 420, y: 0, width: 140, height: 180, color: 'black' });
  const high = 48;
  drawTXTriangleHelper({
    drawer,
    txPadding: { left: 16, top: 10 },
    txGap: { left: 0, top: 8 },
    firstTxTriangle: { x1: 0, y1: 0, x2: 0.866 * high - 10, y2: high / 2, x3: 0, y3: high },
    lineLength: 372,
    high
  });
}

function drawRxCpadModelHelper({ drawer }) {
  drawer.drawRectangle({ x: 0, y: 0, width: 140, height: 210, color: 'black' });
  const lineLength = 372
  const lineStartPoint = { x: 140, y: 24 }
  const line_v_gap = 80;
  const triangleX = 350;
  const v_line1 = 25, v_line2 = 20, h_line = 24;
  const triangle_height = 8, triangle_h_width = 14, triangle_h_gap = 5;
  for (let i = 0; i < 3; i++) {
    // connect channel - rxCapd
    drawer.drawLine({
      x1: lineStartPoint.x,
      y1: lineStartPoint.y + line_v_gap * i,
      x2: lineStartPoint.x + lineLength,
      y2: lineStartPoint.y + line_v_gap * i
    });
    // right circle
    drawer.drawCircle({
      x: lineStartPoint.x + lineLength,
      y: lineStartPoint.y + line_v_gap * i,
      radius: 5,
      color: '#f91414',
      fillColor: '#f91414'
    })
    // vertical 1
    drawer.drawLine({
      x1: triangleX,
      y1: lineStartPoint.y + line_v_gap * i,
      x2: triangleX,
      y2: lineStartPoint.y + line_v_gap * i + v_line1
    });
    // Horizontal 1
    drawer.drawLine({
      x1: triangleX + h_line / 2,
      y1: lineStartPoint.y + line_v_gap * i + v_line1,
      x2: triangleX - h_line / 2,
      y2: lineStartPoint.y + line_v_gap * i + v_line1
    });
    // Horizontal 2
    drawer.drawLine({
      x1: triangleX + h_line / 2,
      y1: lineStartPoint.y + line_v_gap * i + v_line1 + triangle_h_gap,
      x2: triangleX - h_line / 2,
      y2: lineStartPoint.y + line_v_gap * i + v_line1 + triangle_h_gap
    });
    // vertical 2
    drawer.drawLine({
      x1: triangleX,
      y1: lineStartPoint.y + line_v_gap * i + v_line1 + triangle_h_gap,
      x2: triangleX,
      y2: lineStartPoint.y + line_v_gap * i + v_line1 + triangle_h_gap + v_line2
    });
    drawer.drawTriangle({
      x1: triangleX - triangle_h_width / 2,
      y1: lineStartPoint.y + line_v_gap * i + v_line1 + triangle_h_gap + v_line2,
      x2: triangleX + triangle_h_width / 2,
      y2: lineStartPoint.y + line_v_gap * i + v_line1 + triangle_h_gap + v_line2,
      x3: triangleX,
      y3: lineStartPoint.y + line_v_gap * i + v_line1 + triangle_h_gap + v_line2 + triangle_height,
      color: '#005ab3'
    });
  }
}

function drawTXTriangleHelper({ drawer, txPadding, txGap, firstTxTriangle, lineLength, high, isShowStimulus }) {
  for (let i = 0; i < 3; i++) {
    const moveX = txPadding.left + txGap.left * i, moveY = txPadding.top + txGap.top * i;
    const x1 = firstTxTriangle.x1 + moveX,
      y1 = firstTxTriangle.y1 + i * high + moveY,
      x2 = firstTxTriangle.x2 + moveX,
      y2 = firstTxTriangle.y2 + i * high + moveY,
      x3 = firstTxTriangle.x3 + moveX,
      y3 = firstTxTriangle.y3 + i * high + moveY;
    drawer.drawTriangle({ x1, y1, x2, y2, x3, y3 });
    // connect Tx - channel
    drawer.drawLine({ x1: x2, y1: y2, x2: x2 + lineLength, y2 });
    if (isShowStimulus) {
      drawer.drawLine({ x1: moveX - 140, y1: y2, x2: x1 - 20, y2: y2 });
    }
  }
}

function drawTxModelIBISDiagramHelper({ drawer, pinNumber = 3 }) {
  const high = 60;
  const laneGap = { x: 150, y: 32 };
  const txPadding = { left: 30, top: 10 + laneGap.y * 2 },
    txGap = { left: 0, top: 8 },
    firstTxTriangle = { x1: 0, y1: 0, x2: Math.floor(0.866 * high) - 10, y2: high / 2, x3: 0, y3: high },
    lineLength = 30;

  for (let j = 0; j < 3; j++) {
    for (let i = 0; i < pinNumber; i++) {
      const moveX = txPadding.left + txGap.left * i + laneGap.x * j, moveY = txPadding.top + txGap.top * i;
      const x1 = firstTxTriangle.x1 + moveX,
        y1 = firstTxTriangle.y1 + i * high + moveY,
        x2 = firstTxTriangle.x2 + moveX,
        y2 = firstTxTriangle.y2 + i * high + moveY,
        x3 = firstTxTriangle.x3 + moveX,
        y3 = firstTxTriangle.y3 + i * high + moveY;

      drawer.drawLine({ x1: x1 - txPadding.left, y1: y2 - 10, x2: x1, y2: y2 - 10 });
      drawer.drawLine({ x1: x1 - txPadding.left, y1: y2 + 10, x2: x1, y2: y2 + 10 });
      drawer.drawTriangle({ x1, y1, x2, y2, x3, y3, color: 'red' });
      drawer.drawLine({ x1: x2, y1: y2, x2: x2 + lineLength, y2 });
      drawer.drawCircle({ x: x2 + lineLength, y: y2, radius: 2, color: 'black', fillColor: 'black' })

      if (i === pinNumber - 1) {
        const polyLine = [
          [x2 + lineLength, y2 + 1],
          [x2 + lineLength, firstTxTriangle.y1 + high - laneGap.y * Math.abs(j - 2) + 1],
          [firstTxTriangle.x1 + laneGap.x * 3, firstTxTriangle.y1 + high - laneGap.y * Math.abs(j - 2) + 1]
        ];

        drawer.drawPolyline({ points: polyLine });
      }
    }
  }
}

export {
  getDrawer,
  drawHelper,
  drawTxModelHelper,
  drawRxCpadModelHelper,
  drawTxModelIBISDiagramHelper
}